/**
 * The ACTIVE persona, as a tiny external store (#1053 C, made tab-local by 4b).
 *
 * It lived in `crm/experience.ts` and is re-exported from there, so existing
 * importers are unchanged. It remains the tab-local selector for surfaces that
 * explicitly support persona preview (for example entity nouns and authoring).
 * Ordinary experience resolution no longer reads it: #2032 derives the active
 * assignment on the server and accepts an explicit persona only for an
 * administrator preview.
 *
 * ── why the selection stays on the CLIENT ───────────────────────────────────
 * The selection is a preview context, not the caller's assignment. It therefore
 * remains tab-local and may travel only to a door that explicitly performs an
 * administrator preview. Ordinary experience calls omit it and let
 * `core.resolve_experience` derive `user_persona.is_default`; a browser value is
 * never allowed to impersonate an assigned persona.
 *
 * ── 4b: "in this tab" was a claim, and `localStorage` did not honour it ─────
 * The paragraph above was written and shipped while this store used
 * `localStorage`, which is per ORIGIN. So two tabs were never independent: a
 * tab opened after a switch started on the other tab's persona, and the
 * selection outlived the browsing context that made it. The migration's own
 * objection — one tab changing what another is looking at — was answered about
 * the server and left standing about the browser.
 *
 * `sessionStorage` is the storage whose lifetime IS the tab, so it is the one
 * that makes the claim true: the choice survives a reload of THIS tab, is
 * invisible to a sibling tab, and dies when the tab does. (A tab opened with
 * `window.open`/`target=_blank` inherits a COPY of the opener's
 * `sessionStorage`; it is a copy, so a later switch in either does not reach the
 * other, and the property holds.)
 *
 * ── the default, and why it is not simply `'sales'` any more ────────────────
 * FIXTURE mode has no server, and there the persona switcher IS the identity
 * model (`packages/auth/src/react.tsx`), so something has to be chosen before
 * anyone chooses: `'sales'`, exactly as before, and every browser spec that
 * seeds a persona keeps working.
 *
 * LIVE mode starts EMPTY. That prevents preview-aware readers such as entity
 * identity from inventing `sales`, while ordinary experience resolution ignores
 * the selector entirely and always asks the server for the assigned persona.
 */
import { useSyncExternalStore } from 'react'
import { currentPrincipalId, isLive } from '@solum/auth'

const STORAGE_KEY = 'solum.persona'

/**
 * The fixture/demo console's stand-in identity. Deliberately NOT applied in
 * live mode — see the header. Exported so a test can state the distinction
 * rather than restate the string.
 */
export const FIXTURE_PERSONA = 'sales'

/* Guarded, and not defensively: this module is imported by the DATA layer now,
   and the console's unit suites run in node with no DOM at all (there is no
   jsdom in this package). An unguarded storage read at module scope took 11
   unrelated suites down with `ReferenceError: localStorage is not defined` the
   moment `data/queries.ts` reached it — the store has to survive a host that
   has no storage, and remembering nothing is the correct behaviour there. */
/* ── AND IT IS STAMPED WITH THE PRINCIPAL (#2328 §2, ROOT ruling 74071 §7) ──
 *
 * `sessionStorage` is per TAB, not per PERSON, and this shell can change person
 * inside one tab: `UserMenu`'s account switcher ends the session and starts a
 * new authentication in the same browsing context. Measured before the repair,
 * the acting persona was the one piece of personal state that survived that —
 * so #2328 §2's *"a second user on the same browser sees none of the first
 * user's preferences"* was false for it, while the pins, the default and the
 * recents beside it are keyed by user AT THE DOOR (`personaPrefs.ts`).
 *
 * The repair is the shape `recents.ts` took for the same property: remember WHO
 * the value belongs to, and on a mismatch DISCARD rather than migrate. Migrating
 * would be the defect wearing a fix's clothes — #2328 §9 forbids exactly that
 * ("do not migrate anonymous localStorage state into another user's governed
 * profile") — and a preview persona is not the second person's to inherit.
 *
 * ── the stamp is a SECOND key, and that is deliberate ──────────────────────
 * `solum.persona` still holds the bare persona key. Wrapping it in a JSON object
 * would have changed a value that 27 browser specs seed directly and that a
 * person may already have in an open tab, for no gain: the stamp is a separate
 * fact and it reads and writes beside it.
 *
 * ── the limit, stated rather than implied ─────────────────────────────────
 * An UNSTAMPED value is honoured. An absent stamp is not evidence of another
 * actor — it is a value written before this stamp existed, or seeded by a test
 * harness — and refusing it would silently empty every tab that upgraded and
 * every fixture spec that seeds one. Every write from the product path stamps,
 * so the only value that can be inherited is one that predates this code, once,
 * in one tab.
 */
const OWNER_KEY = 'solum.persona.owner'

const stored = (): string | null => {
  try {
    const key = globalThis.sessionStorage?.getItem(STORAGE_KEY) ?? null
    if (key == null || key === '') return null
    const owner = globalThis.sessionStorage?.getItem(OWNER_KEY) ?? null
    if (owner !== null && owner !== currentPrincipalId()) {
      /* A different actor is signed in now. Forget it outright, and clear both
         slots so the next read cannot resurrect it. */
      try {
        globalThis.sessionStorage?.removeItem(STORAGE_KEY)
        globalThis.sessionStorage?.removeItem(OWNER_KEY)
      } catch { /* no storage */ }
      return null
    }
    return key
  } catch { return null }
}
const remember = (k: string): void => {
  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, k)
    const who = currentPrincipalId()
    if (who === null) globalThis.sessionStorage?.removeItem(OWNER_KEY)
    else globalThis.sessionStorage?.setItem(OWNER_KEY, who)
  } catch { /* no storage: this tab only */ }
}

let personaKey = stored() ?? (isLive ? '' : FIXTURE_PERSONA)
const subs = new Set<() => void>()

export function setPersona(k: string) {
  personaKey = k
  remember(k)
  subs.forEach((f) => f())
}

/**
 * Re-read the stored selection for whoever is signed in NOW.
 *
 * The module-scope read happens once, at import, and an account switch does not
 * re-import anything. A host calls this when the principal changes: the stamp
 * decides, so a persona belonging to the previous actor is discarded and the
 * tab falls back to the same opening state a fresh tab would have.
 */
export function resetPersonaForPrincipal(): void {
  const next = stored() ?? (isLive ? '' : FIXTURE_PERSONA)
  if (next === personaKey) return
  personaKey = next
  subs.forEach((f) => f())
}

/** The external-store contract, exported so it can be driven without React. */
export function subscribePersona(cb: () => void): () => void {
  subs.add(cb)
  return () => { subs.delete(cb) }
}

/** Subscribe a component to the active persona. */
export function usePersona(): string {
  return useSyncExternalStore(subscribePersona, currentPersona)
}

/** The active persona OUTSIDE React — for a query function, not a component. */
export function currentPersona(): string {
  return personaKey
}

/**
 * The governed answers that may initialise an EMPTY live tab.
 *
 * This does not decide authority. `core.persona_holders()` already did that
 * under `core.user_persona` RLS, and the personal default already came through
 * `core.my_settings()`. The browser only reconciles those answers with the
 * active catalogue so it never displays a deleted/inactive key as current.
 */
export interface PersonaAssignment {
  persona_key: string
  is_default: boolean
}

export type PersonaReadState = 'loading' | 'ready' | 'refused'

export type PersonaInitialChoice =
  | { state: 'preserved'; key: string }
  | { state: 'waiting' }
  | { state: 'resolved'; key: string; source: 'personal-default' | 'assigned-default' | 'only-assignment' }
  | { state: 'unresolved'; reason: 'assignment-refused' | 'no-valid-assignment' | 'ambiguous-assignment' }

/**
 * Resolve what a fresh authenticated tab may truthfully call "Acting as".
 *
 * A deliberate tab choice always wins and is never overwritten. For an empty
 * tab, the person's governed start preference wins while it still names an
 * available persona. Otherwise the server's assigned default is the same fact
 * ordinary live experience resolution uses. With no valid default, exactly one
 * valid assignment is an honest fallback; zero or many is not a choice the
 * client may invent.
 */
export function personaInitialChoice(input: {
  active: string
  availableKeys: readonly string[] | null
  preference: string | null
  preferenceState: PersonaReadState
  assignments: readonly PersonaAssignment[] | null
  assignmentState: PersonaReadState
}): PersonaInitialChoice {
  if (input.active !== '') return { state: 'preserved', key: input.active }
  if (input.availableKeys === null || input.preferenceState === 'loading') {
    return { state: 'waiting' }
  }

  const available = new Set(input.availableKeys)
  if (input.preference && available.has(input.preference)) {
    return { state: 'resolved', key: input.preference, source: 'personal-default' }
  }

  if (input.assignmentState === 'loading') return { state: 'waiting' }
  if (input.assignmentState === 'refused' || input.assignments === null) {
    return { state: 'unresolved', reason: 'assignment-refused' }
  }

  const valid = input.assignments.filter((row) => available.has(row.persona_key))
  const defaults = valid.filter((row) => row.is_default)
  if (defaults.length === 1) {
    return { state: 'resolved', key: defaults[0]!.persona_key, source: 'assigned-default' }
  }
  if (defaults.length > 1) {
    return { state: 'unresolved', reason: 'ambiguous-assignment' }
  }
  if (valid.length === 1) {
    return { state: 'resolved', key: valid[0]!.persona_key, source: 'only-assignment' }
  }
  return {
    state: 'unresolved',
    reason: valid.length === 0 ? 'no-valid-assignment' : 'ambiguous-assignment',
  }
}

/**
 * Which persona a governed resolve should be made for.
 *
 * `null` rather than a guess when there is nothing to say: the server's own
 * fallback (`user_persona.is_default`) is the right answer then, and sending an
 * empty string would ask it to resolve for a persona that does not exist.
 */
export function personaForResolve(explicit: string | undefined, active: string): string | null {
  const key = (explicit ?? active ?? '').trim()
  return key === '' ? null : key
}
